import { ReactElement, useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { documentationRootGuid } from '@constants'
import { AxiosError } from 'axios'
import CloseIcon from '@mui/icons-material/Close'
import EditIcon from '@mui/icons-material/Edit'
import SaveIcon from '@mui/icons-material/Save'
import { LoadingButton } from '@mui/lab'
import { IconButton, Stack, TextField, Tooltip, Typography } from '@mui/material'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useSnackbar } from '@ui/snackbar'
import { getModel, getProjectFiles } from '@queries'
import { updateElementLabel } from '@mutations'
import { buildErrorMessage } from 'src/constants'

interface Props {
  elementGuid: string
  label: string
  variant?: 'h6' | 'h5' | 'h4' | 'body1' | 'body2'
}

const EditableLabel = ({ elementGuid, label, variant = 'h6' }: Props): ReactElement => {
  const [isEditMode, setIsEditMode] = useState(false)
  const [editedLabel, setEditedLabel] = useState(label)
  const { projectId } = useParams<{ projectId: string }>()
  const { enqueueSnackbar } = useSnackbar()
  const queryClient = useQueryClient()

  // Sync editedLabel when label prop changes
  useEffect(() => {
    if (!isEditMode) {
      setEditedLabel(label)
    }
  }, [label, isEditMode])

  const { mutateAsync: updateLabel, isLoading } = useMutation(
    (newLabel: string) =>
      updateElementLabel.request(projectId as string, elementGuid, {
        label: newLabel,
      }),
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(getModel.getKey(projectId as string))
        await await queryClient.invalidateQueries(
          getProjectFiles.getKey(projectId as string, documentationRootGuid),
        )
        setIsEditMode(false)
        enqueueSnackbar('Label erfolgreich aktualisiert', { variant: 'success' })
      },
      onError: (error: AxiosError) => {
        enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Aktualisieren des Labels'), {
          variant: 'error',
        })
      },
    },
  )

  const handleSave = () => {
    if (editedLabel.trim() !== label.trim()) {
      updateLabel(editedLabel.trim())
    } else {
      setIsEditMode(false)
    }
  }

  const handleAbort = () => {
    setEditedLabel(label)
    setIsEditMode(false)
  }

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleSave()
    } else if (event.key === 'Escape') {
      handleAbort()
    }
  }

  if (isEditMode) {
    return (
      <Stack direction="row" alignItems="center" spacing={1}>
        <TextField
          value={editedLabel}
          onChange={e => setEditedLabel(e.target.value)}
          onKeyDown={handleKeyDown}
          autoFocus
          size="small"
          variant="outlined"
          disabled={isLoading}
          sx={{ flex: 1 }}
        />
        <LoadingButton
          loading={isLoading}
          onClick={handleSave}
          size="small"
          color="primary"
          variant="contained"
          sx={{ minWidth: 28, width: 28, height: 28, p: 0 }}
        >
          <SaveIcon fontSize="small" />
        </LoadingButton>
        <LoadingButton
          loading={isLoading}
          onClick={handleAbort}
          size="small"
          color="error"
          variant="contained"
          sx={{ minWidth: 28, width: 28, height: 28, p: 0 }}
        >
          <CloseIcon fontSize="small" />
        </LoadingButton>
      </Stack>
    )
  }

  return (
    <Stack direction="row" alignItems="center" spacing={1}>
      <Typography variant={variant}>{label}</Typography>
      <Tooltip title="Bauteil umbenennen">
        <IconButton
          onClick={() => setIsEditMode(true)}
          size="small"
          aria-label="Bauteil umbenennen"
        >
          <EditIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Stack>
  )
}

export default EditableLabel
